import { Grid, Typography, useScrollTrigger } from '@mui/material';
import dayjs from 'dayjs';
import * as React from 'react';
import {
  Dispatch,
  ReducerAction,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import ReactGA from 'react-ga';
import UserContext from '../../../shared-components/contexts/UserContext';
import NotationManagementContentPanel from './Components/NotationManagementContentPanel';
import NotationManagementFilterPanel from './Components/NotationManagementFilterPanel';
import { INotationManagement } from './Interfaces/INotationManagement';
import {
  INotationManagementState,
  notationManagementInitialState,
} from './Interfaces/INotationManagementState';
import { NotationManagementReducer } from './NotationManagementReducer';
import { NotationManagementService } from './Services/NotationManagementService';
import { NotationManagementActionType } from './types/NotationManagementActionType';

export const NotationManagementDispatch = React.createContext(
  {} as Dispatch<ReducerAction<typeof NotationManagementReducer>>,
);

export const NotationManagementState = React.createContext(
  {} as INotationManagementState,
);

const NotationManagement = (props: INotationManagement) => {
  const user = React.useContext(UserContext);
  const scrollTrigger = useScrollTrigger();

  const [initalPageLoad, setInitialPageLoad] = useState(true);

  const [state, dispatch] = useReducer(
    NotationManagementReducer,
    notationManagementInitialState(),
  );

  useEffect(() => {
    ReactGA.event({
      category: 'NotationManagement',
      action: 'PageLoad',
      label: JSON.stringify({ insitutionId: user.institutionId }),
      nonInteraction: true,
    });
  }, []);

  useEffect(() => {
    getContent(0).finally(() => {
      setInitialPageLoad(false);
    });
  }, []);

  useEffect(() => {
    if (scrollTrigger) window.scrollTo(0, 100);
  }, [state.filters]);

  const getContent = useCallback(
    (page: number) => {
      return (async () => {
        try {
          if (page !== state.notationsPage) {
            dispatch({
              type: NotationManagementActionType.SetNotationsLoading,
              value: true,
            });
            const notations = await NotationManagementService.getNotationsV2({
              pageSize: 50,
              page: page + 1,
              searchParameters: {
                categoryIds: state.filters.categories.map(x => x.id),
                toDate: state.filters.endDate
                  ? dayjs.utc(state.filters.endDate).endOf('day').toString()
                  : undefined,
                fromDate: state.filters.startDate
                  ? state.filters.startDate.toDateString()
                  : undefined,
                notationTypes: state.filters.notationTypes,
                studentId: state.filters.createdFor?.id,
                createdById: state.filters.createdBy?.id,
                assignedToId: state.filters.assignedTo?.id,
                onlyMyStudents: false,
                orderByDescending: true,
                onlyUnassigned: false,
                alertStatus: state.filters.alertStatus,
              },
            });
            dispatch({
              type: NotationManagementActionType.SetNotations,
              value: [...state.notations, ...notations.items],
            });
            dispatch({
              type: NotationManagementActionType.SetTotalNotations,
              value: notations.totalItems,
            });
          }
        } catch (e) {
          console.log(e);
        } finally {
          dispatch({
            type: NotationManagementActionType.SetNotationsLoading,
            value: false,
          });
        }
      })();
    },
    [state.notations],
  );

  useEffect(() => {
    if (!initalPageLoad) {
      dispatch({
        type: NotationManagementActionType.SetNotationsLoading,
        value: true,
      });
      dispatch({
        type: NotationManagementActionType.SetNotations,
        value: [],
      });
      load();
    }
  }, [state.filters]);

  const load = useCallback(() => {
    NotationManagementService.getNotationsV2({
      pageSize: 50,
      page: 1,
      searchParameters: {
        categoryIds: state.filters.categories.map(x => x.id),
        toDate: state.filters.endDate
          ? dayjs.utc(state.filters.endDate).endOf('day').toString()
          : undefined,
        fromDate: state.filters.startDate
          ? state.filters.startDate.toDateString()
          : undefined,
        notationTypes: state.filters.notationTypes,
        studentId: state.filters.createdFor?.id,
        createdById: state.filters.createdBy?.id,
        assignedToId: state.filters.assignedTo?.id,
        onlyMyStudents: false,
        orderByDescending: true,
        onlyUnassigned: state.filters.onlyUnassigned,
        alertStatus: state.filters.alertStatus,
      },
    })
      .then(n => {
        dispatch({
          type: NotationManagementActionType.SetNotations,
          value: n.items,
        });
        dispatch({
          type: NotationManagementActionType.SetTotalNotations,
          value: n.totalItems,
        });
      })
      .finally(() => {
        dispatch({
          type: NotationManagementActionType.SetNotationsLoading,
          value: false,
        });
      });
  }, [state.filters]);

  return (
    <NotationManagementDispatch.Provider value={dispatch}>
      <NotationManagementState.Provider value={state}>
        <Typography variant="h1" id="notation-management-header">
          Notation Management
        </Typography>
        <Grid container spacing={4}>
          <Grid item md={12} lg={4}>
            <NotationManagementFilterPanel filters={state.filters} />
          </Grid>
          <Grid item md={12} lg={8}>
            <NotationManagementContentPanel
              filters={state.filters}
              getContent={getContent}
              items={state.notations}
            />
          </Grid>
        </Grid>
      </NotationManagementState.Provider>
    </NotationManagementDispatch.Provider>
  );
};

export default NotationManagement;
